import {
  getPayment,
  getRefund,
  cancelOpenBankingRefund,
  getTransactionLabels,
  setTransactionLabel,
  removeTransactionLabel,
} from "@api";
import { updatePayment, useMerchant, useUser } from "@auth";
import { Button, Drawer, DrawerBody, DrawerFooter } from "@components";
import { toast } from "@components/toast/Toast";
import { usePaymentRequestLabels } from "@flags";
import { useUserName } from "@hooks";
import { formatAmount, formatDateTime } from "@utils";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Clipboard, ClipboardCheck, Star, StarFill } from "react-bootstrap-icons";
import IssueCardRefund from "../../flows/IssueCardRefund";
import IssueKlarnaRefund from "../../flows/IssueKlarnaRefund";
import IssuePayByBankRefund from "../../flows/IssuePayByBankRefund";
import AuthoriseRefund from "../../flows/AuthoriseRefund";
import PaymentDetails from "./components/PaymentDetails";
import RefundDetails from "./components/RefundDetails";
import UnreferencedRefundDetails from "./components/UnreferencedRefundDetails";

const RECONCILED_LABEL = "ReconciledInErp";

const TransactionDetails = ({
  id,
  paymentMethodKey,
  transactionType,
  refundId,
  onClose,
  onChange,
  onNavigate,
}) => {
  const [payment, setPayment] = useState(null);
  // A refund with no payment behind it (unreferenced POS refund); shown instead of `payment`.
  const [unreferencedRefund, setUnreferencedRefund] = useState(null);
  const { hasPermission } = useMerchant();
  const [showCardRefund, setShowCardRefund] = useState(false);
  const [showOBRefund, setShowOBRefund] = useState(false);
  const [showKlarnaRefund, setShowKlarnaRefund] = useState(false);
  const [editingRefund, setEditingRefund] = useState(null);
  const [showAuthorise, setShowAuthorise] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const [labels, setLabels] = useState([]);
  const labelsEnabled = usePaymentRequestLabels();
  const { user } = useUser();
  const queryClient = useQueryClient();

  const reconciledLabel = labels.find((l) => l.type === RECONCILED_LABEL);
  const isReconciled = !!reconciledLabel;

  // Resolved here rather than by the API: the client already holds a cached user list.
  const reconciledByName = useUserName(reconciledLabel?.updatedByUserGuid);

  // Hovering answers "has this been logged, and by whom?" without opening anything else.
  const reconciledTitle = reconciledLabel
    ? [
        "Reconciled in ERP",
        reconciledByName ? ` by ${reconciledByName}` : "",
        reconciledLabel.updatedOn
          ? ` on ${formatDateTime(reconciledLabel.updatedOn)}`
          : "",
        " — click to unmark",
      ].join("")
    : "Mark as reconciled in ERP";

  const isRefund = transactionType === "Refund";

  // Refund rows without a payment id are unreferenced POS refunds: they are loaded on their
  // own rather than through a payment.
  const isUnreferencedRefund = isRefund && !id && !!refundId;

  // The refund this drawer represents (refund rows only).
  const refund = isRefund
    ? payment?.refunds?.find((r) => r.id === refundId)
    : null;
  const isInitialised = refund && refund.status === "Initialized";

  // For refund rows the drawer's copyable header id is the refund's own id;
  // for payments it's the transaction (payment) id.
  const headerId = isRefund && refundId ? refundId : payment?.id;

  // The label endpoints route on the payment id and narrow with refundId, resolving to the
  // same transaction the header id names. Derived from isRefund so the two cannot drift.
  // With no payment, the route id is the refund itself.
  const labelRefundId = isRefund ? refundId : undefined;
  const labelRouteId = id ?? (isUnreferencedRefund ? refundId : null);

  const isOpen = !!payment || !!unreferencedRefund;

  const copyId = () => {
    navigator.clipboard.writeText(headerId);
    setIdCopied(true);
    setTimeout(() => setIdCopied(false), 2000);
  };

  useEffect(() => {
    if (isUnreferencedRefund) {
      setPayment(null);
      let stale = false;
      getRefund(refundId)
        .then((data) => !stale && setUnreferencedRefund(data))
        .catch(() => !stale && setUnreferencedRefund(null));
      return () => {
        stale = true;
      };
    }

    setUnreferencedRefund(null);

    // Clearing the selection (e.g. mid-navigation) closes the drawer so it can
    // re-open on the next selection — giving a shut-then-open transition.
    if (!id || !paymentMethodKey) {
      setPayment(null);
      return;
    }

    async function fetchData() {
      const data = await getPayment(id, paymentMethodKey);
      setPayment(data);
    }

    fetchData();
  }, [id, paymentMethodKey, isUnreferencedRefund, refundId]);

  useEffect(() => {
    if (!labelsEnabled || !labelRouteId) {
      setLabels([]);
      return;
    }

    // Fetched rather than taken from the table row: the drawer is also opened by
    // navigating between linked transactions, where no row is in hand.
    let stale = false;

    getTransactionLabels(labelRouteId, labelRefundId)
      .then((data) => !stale && setLabels(data || []))
      .catch(() => !stale && setLabels([]));

    return () => {
      stale = true;
    };
  }, [labelRouteId, labelRefundId, labelsEnabled]);

  // Keeps the table's read-only star in step without refetching the whole page. Rows are
  // matched on their transaction id: the refund id on refund rows, the payment id otherwise.
  const patchTableStar = (reconciled) => {
    queryClient.setQueriesData({ queryKey: ["transactions"] }, (old) =>
      old?.data
        ? {
            ...old,
            data: old.data.map((r) =>
              (r.refundId ?? r.paymentId) === (labelRefundId ?? id)
                ? {
                    ...r,
                    labels: reconciled
                      ? [...(r.labels || []), RECONCILED_LABEL]
                      : (r.labels || []).filter((t) => t !== RECONCILED_LABEL),
                  }
                : r,
            ),
          }
        : old,
    );
  };

  const toggleReconciled = async () => {
    const next = !isReconciled;
    const previous = labels;

    // Optimistic — the star is the acknowledgement that the id was logged in the ERP,
    // so it should flip under the cursor rather than after a round trip. The stand-in
    // attribution matches what the server will record.
    setLabels(
      next
        ? [
            ...labels,
            {
              type: RECONCILED_LABEL,
              updatedOn: new Date().toISOString(),
              // user.id is the auth id; user.guid is what labels are attributed to.
              updatedByUserGuid: user?.guid ?? null,
            },
          ]
        : labels.filter((l) => l.type !== RECONCILED_LABEL),
    );
    patchTableStar(next);

    try {
      if (next) {
        await setTransactionLabel(labelRouteId, labelRefundId, RECONCILED_LABEL);
      } else {
        await removeTransactionLabel(labelRouteId, labelRefundId, RECONCILED_LABEL);
      }
    } catch {
      setLabels(previous);
      patchTableStar(!next);
      toast({
        description: "Could not update the reconciled flag. Please try again.",
      });
    }
  };

  const startRefund = () => {
    if (paymentMethodKey === "card") {
      setShowCardRefund(true);
    } else if (paymentMethodKey === "paybybank") {
      setShowOBRefund(true);
    } else if (paymentMethodKey === "klarna") {
      setShowKlarnaRefund(true);
    }
  };

  const close = () => {
    setPayment(null);
    setUnreferencedRefund(null);
    onClose();
  };

  const reloadData = async () => {
    const data = await getPayment(id, paymentMethodKey);
    setPayment(data);
    if (onChange) {
      onChange();
    }
  };

  const closeOBModal = () => {
    setShowOBRefund(false);
    setEditingRefund(null);
  };

  // A pay-by-bank refund was created or edited.
  const handleOpenBankingRefundSaved = async ({
    refundId: savedId,
    isEdit,
  }) => {
    if (isEdit) {
      // Stay on this refund; just refresh so the edited details show.
      await reloadData();
      return;
    }

    // New refund: refresh the table, then route into its details drawer. The
    // toast has already fired (in the modal); onNavigate shuts this drawer and
    // re-opens on the refund after a short delay for the transition.
    onChange?.();
    onNavigate?.({
      transactionType: "Refund",
      paymentId: payment.id,
      refundId: savedId,
      paymentMethodKey,
    });
  };

  const cancelRefund = async () => {
    if (!refund) return;
    const response = await cancelOpenBankingRefund(refund.id);
    if (response.status === 204) {
      toast({
        description: `Cancelled refund for ${formatAmount(refund.amount)}`,
      });
      await reloadData();
    }
  };

  return (
    <Drawer
      title={
        <div>
          <div className="text-lg font-semibold">
            {transactionType === "Payment"
              ? "Payment details"
              : transactionType === "Refund"
                ? "Refund details"
                : "Transaction details"}
          </div>
          {isOpen && (
            <div className="flex items-center gap-2 mt-0.5">
              {/* Leads the row so it holds one position regardless of how long the id renders. */}
              {labelsEnabled && (
                <button
                  type="button"
                  onClick={toggleReconciled}
                  title={reconciledTitle}
                  aria-pressed={isReconciled}
                  className="transition-colors"
                >
                  {isReconciled ? (
                    <StarFill className="size-4 text-amber-400" />
                  ) : (
                    <Star className="size-4 text-zinc-300 hover:text-amber-400" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={copyId}
                title={
                  isRefund ? "Copy Refund ID" : "Copy Ontik Transaction ID"
                }
                className="text-primary hover:text-primary/80 transition-colors"
              >
                {idCopied ? (
                  <ClipboardCheck className="w-3.5 h-3.5" />
                ) : (
                  <Clipboard className="w-3.5 h-3.5" />
                )}
              </button>
              <span className="text-xs text-zinc-600 font-normal">
                {isRefund ? "Refund ID" : "ID"}: {headerId}
              </span>
            </div>
          )}
        </div>
      }
      isOpen={isOpen}
      toggle={close}
    >
      {unreferencedRefund && (
        <DrawerBody>
          <UnreferencedRefundDetails
            refund={unreferencedRefund}
            reconciledLabel={reconciledLabel}
            reconciledByName={reconciledByName}
          />
        </DrawerBody>
      )}
      {payment && (
        <>
          <DrawerBody>
            {isRefund ? (
              <RefundDetails
                payment={payment}
                refundId={refundId}
                paymentMethodKey={paymentMethodKey}
                onNavigate={onNavigate}
                onEdit={(r) => setEditingRefund(r)}
                reconciledLabel={reconciledLabel}
                reconciledByName={reconciledByName}
              />
            ) : (
              <PaymentDetails
                payment={payment}
                paymentMethodKey={paymentMethodKey}
                onNavigate={onNavigate}
                reconciledLabel={reconciledLabel}
                reconciledByName={reconciledByName}
              />
            )}
          </DrawerBody>
          {!isRefund && payment.status !== "Refunded" && (
            <DrawerFooter>
              {hasPermission(updatePayment) && (
                <Button
                  className="ml-auto"
                  color="secondary"
                  onClick={() => startRefund()}
                >
                  Issue a refund
                </Button>
              )}
            </DrawerFooter>
          )}
          {isRefund && isInitialised && (
            <DrawerFooter>
              <Button color="secondary" onClick={cancelRefund}>
                Cancel refund
              </Button>
              <Button
                className="ml-auto"
                color="primary"
                onClick={() => setShowAuthorise(true)}
              >
                Authorise refund
              </Button>
            </DrawerFooter>
          )}
          <IssueCardRefund
            isOpen={showCardRefund}
            isRefundAllowed={payment.isRefundAllowed}
            onClose={() => setShowCardRefund(false)}
            onComplete={() => reloadData()}
            amountPaid={payment.amount}
            paymentId={payment.id}
            amountRefunded={payment.amountRefunded}
            customerName={payment.customerName}
          />
          <IssueKlarnaRefund
            isOpen={showKlarnaRefund}
            isRefundAllowed={payment.isRefundAllowed}
            onClose={() => setShowKlarnaRefund(false)}
            onComplete={() => reloadData()}
            amountPaid={payment.amount}
            paymentId={payment.id}
            amountRefunded={payment.amountRefunded}
            customerName={payment.customerName}
          />
          <IssuePayByBankRefund
            isOpen={showOBRefund || !!editingRefund}
            payment={payment}
            refund={editingRefund}
            onClose={closeOBModal}
            onSaved={handleOpenBankingRefundSaved}
          />
          <AuthoriseRefund
            isOpen={showAuthorise}
            refund={refund}
            onClose={() => setShowAuthorise(false)}
            onComplete={() => reloadData()}
          />
        </>
      )}
    </Drawer>
  );
};

export default TransactionDetails;
